/**
 * Ladies Hub Premium Configuration
 * Centralized motion, animation, and feature flag settings
 * Single source of truth for all Ladies Hub configuration
 */

import LadiesHubCategoriesJSON from './LadiesHubCategories.json';

// ============================================
// THEME & COLORS
// ============================================
export const LadiesHubColors = {
  primary: "#F59E0B",
  primary2: "#FB923C",
  ink: "#111827",
  muted: "#6B7280",
  surface: "#FFFFFF",
  bg: "#FFF7ED",
  chipBg: "rgba(245,158,11,0.10)",
  chipBorder: "rgba(245,158,11,0.25)",
  headerShadow: "rgba(0,0,0,0.08)",
  cardShadow: "rgba(15,23,42,0.12)",
  verified: "#10B981",
  dark: {
    bg: "#1C1917",
    surface: "#292524",
    ink: "#F5F5F4",
    muted: "#A8A29E",
  }
};

// ============================================
// MOTION CONFIGURATION
// ============================================
export const LadiesHubMotion = {
  // Spring physics for chip bar and cards
  spring: { 
    type: "spring", 
    stiffness: 420, 
    damping: 34, 
    mass: 0.9 
  },
  // Softer spring for sheets and modals
  springSoft: { 
    type: "spring", 
    stiffness: 260, 
    damping: 30, 
    mass: 1.0 
  },
  // Fade slide for content transitions
  fadeSlide: { 
    duration: 0.22, 
    ease: [0.2, 0.8, 0.2, 1] 
  },
  // Entrance animation delays
  staggerChildren: 0.03,
  // FAB entrance
  fabEntrance: {
    type: "spring",
    stiffness: 400,
    damping: 25
  }
};

// ============================================
// RADII & SPACING
// ============================================
export const LadiesHubRadii = {
  card: 18,
  chip: 999,
  sheet: 24,
  image: 16,
  button: 12
};

export const LadiesHubSpacing = {
  xs: 6,
  sm: 10,
  md: 14,
  lg: 18,
  xl: 24,
  xxl: 32
};

// ============================================
// SHADOWS
// ============================================
export const LadiesHubShadows = {
  header: "0 8px 20px rgba(0,0,0,0.08)",
  card: "0 12px 28px rgba(15,23,42,0.12)",
  chipBar: "0 10px 30px rgba(15,23,42,0.10)",
  fab: "0 8px 24px rgba(245, 158, 11, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)"
};

// ============================================
// FEATURE FLAGS
// ============================================
export const LadiesHubFeatures = {
  // UI Features
  enableSkeletonLoading: true,
  enableHeaderShadowOnScroll: true,
  enableVerifiedBadges: true,
  enableFeaturedSellersCarousel: true,
  minVerifiedSellersForCarousel: 3,
  
  // Curated Rows
  enableCuratedRows: true,
  curatedRowsOnlyOnAllTab: true,
  
  // Animation Features
  enableSpringAnimations: true,
  enableHapticFeedback: true,
  enableCardHoverLift: true,
  
  // Category Behavior
  autoOpenSubcategorySheet: true,
  
  // Monetization Placeholders (structure only)
  featuredSellerSlots: {
    enabled: false,
    maxSlots: 5,
    pricePerDay: 0
  },
  adBoostOptions: {
    enabled: false,
    durations: ["24h", "3d", "7d"]
  },
  
  // Safety & Trust
  enableSafetyNotice: true,
  enableReportButton: true
};

// ============================================
// SKELETON SHIMMER CONFIG
// ============================================
export const LadiesHubSkeleton = {
  shimmerDuration: "1.5s",
  shimmerGradient: {
    light: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    dark: "linear-gradient(90deg, #44403C 25%, #57534E 50%, #44403C 75%)"
  },
  cardCount: 6
};

// ============================================
// CATEGORY DATA (from JSON)
// ============================================
export const LadiesHubCategories = LadiesHubCategoriesJSON;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get main categories array (includes 'all' virtual tab)
 */
export function getMainCategories() {
  return LadiesHubCategories.mainCategories;
}

/**
 * Get main categories for chip bar (used by LadiesHubChipBar)
 */
export function getChipBarTabs() {
  return LadiesHubCategories.mainCategories.map(cat => ({
    id: cat.id,
    label_en: cat.label.en,
    label_ar: cat.label.ar,
    icon: cat.icon,
    subcategories: (cat.subcategories || []).map(sub => ({
      id: sub.id,
      en: sub.label.en,
      ar: sub.label.ar,
      helper_en: sub.helper?.en || '',
      helper_ar: sub.helper?.ar || '',
    })),
    viewAllLabel: cat.viewAllLabel
  }));
}

/**
 * Get category label by ID
 */
export function getCategoryLabel(categoryId, lang = 'en') {
  const main = LadiesHubCategories.mainCategories.find(m => m.id === categoryId);
  if (main) return main.label[lang] || main.label.en;
  
  // Check subcategories
  for (const m of LadiesHubCategories.mainCategories) {
    const sub = (m.subcategories || []).find(s => s.id === categoryId);
    if (sub) return sub.label[lang] || sub.label.en;
  }
  return categoryId;
}

/**
 * Get main category by ID
 */
export function getMainCategoryById(mainId) {
  return LadiesHubCategories.mainCategories.find(m => m.id === mainId);
}

/**
 * Get subcategory label
 */
export function getSubcategoryLabel(mainId, subId, lang = 'en') {
  const main = getMainCategoryById(mainId);
  if (!main) return subId;
  const sub = (main.subcategories || []).find(s => s.id === subId);
  return sub ? (sub.label[lang] || sub.label.en) : subId;
}

/**
 * Build category path for API
 */
export function buildCategoryPath(mainId, subId) {
  return `ladies/${mainId}/${subId}`;
}

/**
 * Parse category path
 */
export function parseCategoryPath(path) {
  if (!path) return { hub: null, mainId: null, subId: null };
  const parts = path.split('/');
  if (parts[0] === 'ladies' && parts.length >= 3) {
    return { hub: 'ladies', mainId: parts[1], subId: parts[2] };
  }
  return { hub: null, mainId: null, subId: null };
}

/**
 * Get all subcategories as flat array
 */
export function getAllSubcategoriesFlat(lang = 'en') {
  const result = [];
  LadiesHubCategories.mainCategories.forEach(main => {
    if (main.isVirtual) return;
    (main.subcategories || []).forEach(sub => {
      result.push({
        mainId: main.id,
        mainLabel: main.label[lang],
        mainIcon: main.icon,
        subId: sub.id,
        subLabel: sub.label[lang],
        categoryPath: `ladies/${main.id}/${sub.id}`
      });
    });
  });
  return result;
}

/**
 * Get curated rows config
 */
export function getCuratedRows() {
  return LadiesHubCategories.curatedRows;
}

/**
 * Get curated row label
 */
export function getCuratedRowLabel(row, lang = 'en') {
  return row.label?.[lang] || row.label?.en || row.id;
}

/**
 * Get search suggestions
 */
export function getSearchSuggestions(lang = 'en') {
  return LadiesHubCategories.searchSuggestions[lang] || LadiesHubCategories.searchSuggestions.en;
}

/**
 * Get hub microcopy
 */
export function getHubMicrocopy(key, lang = 'en') {
  const copy = LadiesHubCategories.hubMicrocopy?.[key];
  if (!copy) return '';
  return copy[lang] || copy.en || '';
}

// ============================================
// COMBINED THEME EXPORT (for backwards compat)
// ============================================
export const LadiesHubTheme = {
  colors: LadiesHubColors,
  motion: LadiesHubMotion,
  radii: LadiesHubRadii,
  spacing: LadiesHubSpacing,
  shadows: LadiesHubShadows,
  features: LadiesHubFeatures,
  skeleton: LadiesHubSkeleton
};

export default LadiesHubTheme;
